import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import "../../../styles/pages/ouber.scss";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Calendar,
  Table,
  Tag,
  Button,
  Typography,
  Modal,
  Space,
  Alert,
  Select
} from "antd";
import {
  PlusCircleOutlined,
  CalendarOutlined,
  TableOutlined,
  EyeOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  SyncOutlined
} from "@ant-design/icons";
import TabSelection from "../../../components/SelectContent/TabSelection";
import { setOuberAdminTab } from "../../../state/slices/generalSlice";
import ManageTripsPage from "./ManageTripsPage";
import AdminViewTripModal from "../../../components/Ouber/modals/admin/AdminViewTripModal";
import AdminEditTripModal from "../../../components/Ouber/modals/admin/AdminEditTripModal";
import AdminAddTripModal from "../../../components/Ouber/modals/admin/AdminAddTripModal";
import AdminConfirmDeleteTripModal from "../../../components/Ouber/modals/admin/AdminConfirmDeleteTripModal";
import { useOuberService } from "../../../services";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";
import { officeAcronyms, mapUnitToAcronym } from "../../../utils/ouberUtils";

const { Title } = Typography;
dayjs.extend(customParseFormat);

/**
 * The purpose of this page is to provide administrators with a centralized 
 * calendar view of all scheduled trips across the Ouber system.
 */
const AdminCalendarOverviewPage = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  
  const [isDayModalVisible, setIsDayModalVisible] = useState(false);
  const [isTripModalVisible, setIsTripModalVisible] = useState(false);
  const [isEditTripModalVisible, setIsEditTripModalVisible] = useState(false);
  const [isAddTripModalVisible, setIsAddTripModalVisible] = useState(false);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { service: ouberService } = useOuberService();
  const currentTab = useSelector((state) => state.generalSlice?.ouberAdminCalTab);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);

  const userUnits = useMemo(() => {
    if (!profile) return [];
    const todayStr = dayjs().format("YYYY-MM-DD");
    const units = (profile.positions || [])
      .filter(p => {
        const isAO = [
          'Administrative Officer I',
          'Administrative Officer II',
          'Administrative Officer III',
          'Administrative Officer IV',
          'Administrative Officer V',
        ].includes(p.position_name);
        if (!isAO) return false;

        const endDate = p.pivot?.position_end_date || p.position_end_date;
        if (!endDate) return true;
        return endDate >= todayStr;
      })
      .map(p => (p.unit ? mapUnitToAcronym(p.unit) : null))
      .filter(Boolean);

    if (units.length === 0 && profile.department) {
      return [mapUnitToAcronym(profile.department)];
    }
    
    return units;
  }, [profile]);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  const [tripData, setTripData] = useState([]);
  const [unitFilter, setUnitFilter] = useState('');

  const filteredData = tripData; // Using tripData directly as it is filtered by the backend

  // Fetches trip data from the backend with support for unit-based filtering
  const loadTrips = useCallback(async (unit = '') => {
    try {
      const params = unit ? { unit } : {};
      const response = await ouberService.getTrips(params);
      const tripsListRaw = Array.isArray(response) ? response : (response?.data || []);
      const tripsList = tripsListRaw.filter(t => {
        const status = t.trip_status || t.status;
        return status !== 'Cancelled by AO';
      });
      
      // Formats the raw backend trip data into objects for display
      const trips = tripsList.map(t => ({
        ...t,
        key: t.trip_id.toString(),
        id: t.trip_id.toString(),
        pickup: t.pickup_venue,
        destination: t.dropoff_location,
        date: t.trip_date ? t.trip_date.split('T')[0] : null,
        time: t.pickup_time ? (() => { const h = parseInt(t.pickup_time.split(":")[0]); return (h % 12 || 12) + ":" + t.pickup_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : null,
        estimatedArrival: t.estimated_arrival_time ? (() => { const h = parseInt(t.estimated_arrival_time.split(":")[0]); return (h % 12 || 12) + ":" + t.estimated_arrival_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : null,
        status: t.trip_status,
        driver: t.driver,
        vehicle: t.vehicle,
        driverName: (t.driver && typeof t.driver === 'object' && t.driver.driver_name) ? t.driver.driver_name : (t.custom_driver_name || "Unassigned"),
        driverContact: (t.driver && typeof t.driver === 'object' && t.driver.contact_number) ? t.driver.contact_number : (t.custom_driver_contact || "N/A"),
        vehicleModel: (t.vehicle && typeof t.vehicle === 'object' && t.vehicle.vehicle_model) ? t.vehicle.vehicle_model : (t.custom_vehicle_name || "Unassigned"),
        vehicleType: (t.vehicle && typeof t.vehicle === 'object' && t.vehicle.vehicle_type) ? t.vehicle.vehicle_type : (t.custom_vehicle_type || "N/A"),
        plateNumber: (t.vehicle && typeof t.vehicle === 'object' && t.vehicle.plate_number) ? t.vehicle.plate_number : (t.custom_vehicle_plate || "N/A"),
        capacity: `${t.passengers ? t.passengers.length : 0}/${t.trip_passenger_capacity}`,
        purpose: t.trip_purpose,
        organizing_unit: t.organizing_unit,
      }));
      setTripData(trips);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load calendar trips.");
    }
  }, [ouberService]);

  const loadTripsRef = useRef(loadTrips);
  useEffect(() => {
    loadTripsRef.current = loadTrips;
  }, [loadTrips]);

  // Debounced fetching whenever the unit search filter changes
  useEffect(() => {
    const handler = setTimeout(() => {
      if (currentTab === 'calendar' || !currentTab) {
        loadTripsRef.current(unitFilter);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [unitFilter, currentTab]);

  // Fetch data immediately when the window regains focus to keep legends accurate across different accounts
  useEffect(() => {
    const handleFocus = () => {
      if (currentTab === 'calendar' || !currentTab) {
        loadTripsRef.current(unitFilter);
      }
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [unitFilter, currentTab]);
  /* --- MODAL HANDLERS --- */
  const handleDayClick = useCallback((date) => {
    setSelectedDate(date);
    setIsDayModalVisible(true);
  }, []);

  const handleTripClick = useCallback((e, trip) => {
    if (e && e.stopPropagation) e.stopPropagation(); // Prevent opening the day modal
    setSelectedTrip(trip);
    setIsDayModalVisible(false); // Close the list of trips modal for better visibility
    setIsTripModalVisible(true);
  }, []);

  const dateCellRender = useCallback((value) => {
    const dateString = value.format("YYYY-MM-DD");
    // Find all trips for this specific day and sort by time
    const dayTrips = tripData
      .filter((trip) => trip.date === dateString)
      .sort((a, b) => {
        const timeA = a.pickup_time || "00:00:00";
        const timeB = b.pickup_time || "00:00:00";
        return timeA.localeCompare(timeB);
      });

    return (
      <div 
        style={{ minHeight: "80px", padding: "4px" }} 
      >
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {dayTrips.map((trip) => (
            <li key={trip.id} style={{ marginBottom: "2px" }}>
              <div
                onClick={(e) => handleTripClick(e, trip)}
                className={`trip-card-block ${(() => {
                  if (trip.status === "Completed" || trip.status === "Completed Trip") return "legend-completed";
                  if (trip.status === "In Progress") return "legend-pending";
                  const totalQueued = trip.passengers?.length || 0;
                  const isFull = totalQueued >= (Number(trip.trip_passenger_capacity) || 0);

                  const dDate = trip.deadline_date || (trip.trip_date ? dayjs(trip.trip_date).subtract(1, 'day').format('YYYY-MM-DD') : null);
                  const dTime = trip.deadline_time || "17:00:00";
                  let isDeadlinePassed = false;
                  if (dDate) {
                    const deadline = dayjs(`${dDate} ${dTime}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm a"]);
                    isDeadlinePassed = dayjs().isAfter(deadline);
                  }

                  return (isFull || isDeadlinePassed) ? "legend-unavailable" : "legend-available";
                })()}`}
              >
                <span className="trip-time" style={{ fontWeight: 600 }}>{trip.time}</span>
                <span className="trip-dest" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {trip.pickup} <ArrowRightOutlined style={{ fontSize: '8px' }} /> {trip.destination}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [tripData, handleTripClick]);

  const calendarHeaderRender = useCallback(({ value, onChange }) => {
    const localeData = value.localeData();
    const months = localeData.monthsShort();
    const monthOptions = [];

    for (let i = 0; i < 12; i++) {
        monthOptions.push(
            <Select.Option key={i} value={i}>
                {months[i]}
            </Select.Option>,
        );
    }

    const year = value.year();
    const month = value.month();
    const yearOptions = [];
    for (let i = 2026; i < 2026 + 11; i += 1) {
        yearOptions.push(
            <Select.Option key={i} value={i}>
                {i}
            </Select.Option>,
        );
    }

    return (
        <div style={{ padding: '8px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button 
                icon={<SyncOutlined />} 
                onClick={() => loadTrips(unitFilter)}
                title="Refresh Calendar"
            />
            <Select
                size="middle"
                dropdownMatchSelectWidth={false}
                value={year}
                style={{ width: 90 }}
                onChange={(newYear) => {
                    const now = value.clone().year(newYear);
                    onChange(now);
                }}
            >
                {yearOptions}
            </Select>
            <Select
                size="middle"
                dropdownMatchSelectWidth={false}
                value={month}
                style={{ width: 90 }}
                onChange={(newMonth) => {
                    const now = value.clone().month(newMonth);
                    onChange(now);
                }}
            >
                {monthOptions}
            </Select>
            <Select
                size="middle"
                showSearch
                placeholder="Search unit..."
                allowClear
                value={unitFilter || undefined}
                onChange={value => setUnitFilter(value || '')}
                style={{ width: 250 }}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={Object.keys(officeAcronyms).sort().map(key => ({
                  value: key,
                  label: `${key} - ${officeAcronyms[key]}`
                }))}
            />
        </div>
    );
  }, [unitFilter, loadTrips]);

  const getStatusTag = (status) => {
    let color = status === "Scheduled" ? "blue" : "orange";
    return (
      <Tag color={color} style={{ borderRadius: "12px" }}>
        {status}
      </Tag>
    );
  };

  const columns = [
    { title: "Pickup", dataIndex: "pickup", key: "pickup" },
    {
      title: "",
      key: "arrow",
      render: () => <ArrowRightOutlined style={{ color: '#8c8c8c' }} />,
      width: 30
    },
    { title: "Destination", dataIndex: "destination", key: "destination" },
    { title: "Pickup Time", dataIndex: "time", key: "time" },
    { title: "Estimated Arrival", dataIndex: "estimatedArrival", key: "estimatedArrival" },
    { title: "Unit", dataIndex: "organizing_unit", key: "organizing_unit" },
    { 
      title: "Capacity", 
      dataIndex: "capacity", 
      key: "capacity",
      render: (capacity) => {
        const isFull = capacity && capacity.split('/')[0] === capacity.split('/')[1];
        return <Tag color={isFull ? "error" : "default"}>{capacity || "N/A"}</Tag>;
      }
    },
    { title: "Date", dataIndex: "date", key: "date" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const dDate = record.deadline_date || (record.trip_date ? dayjs(record.trip_date).subtract(1, 'day').format('YYYY-MM-DD') : null);
        const dTime = record.deadline_time || "17:00:00";
        let isDeadlinePassed = false;
        if (dDate) {
          const deadline = dayjs(`${dDate} ${dTime}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm a"]);
          isDeadlinePassed = dayjs().isAfter(deadline);
        }
        
        const totalQueued = record.passengers?.length || 0;
        const capacityVal = record.trip_passenger_capacity || (record.capacity ? record.capacity.split('/')[1] : 0);
        const isFull = totalQueued >= Number(capacityVal);
        
        if (isDeadlinePassed || isFull) {
           return <Tag color="error" style={{ borderRadius: "12px" }}>Unavailable</Tag>;
        }
        
        return getStatusTag(status);
      }
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />} 
          onClick={(e) => handleTripClick(e, record)} 
        />
      ),
    }
  ];

  /* --- RENDER DETAILS MODAL --- */
  const renderTripModal = () => (
    <AdminViewTripModal 
      isOpen={isTripModalVisible}
      onClose={() => setIsTripModalVisible(false)}
      tripData={selectedTrip}
      onUpdateSuccess={async () => {
        // Refresh the main trips list
        await loadTrips(unitFilter);
        // Also refresh the selected trip details to pick up passenger changes
        if (selectedTrip) {
          try {
            const freshTrip = await ouberService.getTripDetails(selectedTrip.trip_id);
            // Format fresh trip back to UI format
            const formatted = {
              ...freshTrip,
              key: freshTrip.trip_id.toString(),
              id: freshTrip.trip_id.toString(),
              pickup: freshTrip.pickup_venue,
              destination: freshTrip.dropoff_location,
              date: freshTrip.trip_date ? freshTrip.trip_date.split('T')[0] : null,
              time: freshTrip.pickup_time ? (() => { const h = parseInt(freshTrip.pickup_time.split(":")[0]); return (h % 12 || 12) + ":" + freshTrip.pickup_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : null,
              status: freshTrip.trip_status,
              driverName: freshTrip.driver ? freshTrip.driver.driver_name : (freshTrip.custom_driver_name || "Unassigned"),
              driverContact: freshTrip.driver ? freshTrip.driver.contact_number : (freshTrip.custom_driver_contact || "N/A"),
              vehicle: freshTrip.vehicle ? freshTrip.vehicle.vehicle_model : (freshTrip.custom_vehicle_name || "Unassigned"),
              plateNumber: freshTrip.vehicle ? freshTrip.vehicle.plate_number : (freshTrip.custom_vehicle_plate || "N/A"),
              capacity: `${freshTrip.passengers ? freshTrip.passengers.length : 0}/${freshTrip.trip_passenger_capacity}`,
            };
            setSelectedTrip(formatted);
          } catch (e) {
            loadTrips(unitFilter); // Fallback to list refresh
          }
        }
      }}

      onEditTrip={(trip) => {
        setIsTripModalVisible(false);
        setSelectedTrip(trip);
        setIsEditTripModalVisible(true);
      }}
      onDeleteTrip={(trip) => {
        setIsTripModalVisible(false);
        setIsDeleteConfirmVisible(true);
      }}
    />
  );

  /* --- RENDER DAY ITINERARY MODAL --- */
  const renderDayModal = () => {
    const dateString = selectedDate ? selectedDate.format("YYYY-MM-DD") : "";
    const dayTrips = filteredData
      .filter((trip) => trip.date === dateString)
      .sort((a, b) => {
        const timeA = dayjs(a.time, ["h:mm a", "HH:mm:ss"]);
        const timeB = dayjs(b.time, ["h:mm a", "HH:mm:ss"]);
        return timeA.diff(timeB);
      });

    return (
      <Modal
        title={<Title level={4} style={{ color: 'white', margin: 0 }}>List of Trips for {selectedDate ? selectedDate.format("MMMM D, YYYY") : ""}</Title>}
        open={isDayModalVisible}
        onCancel={() => setIsDayModalVisible(false)}
        footer={null}
        width={1000}
        closeIcon={<CloseOutlined style={{ color: "white" }} />}
        styles={{
          header: {
            backgroundColor: "#862633",
            padding: "16px 24px",
            borderRadius: "8px 8px 0 0",
            margin: "-20px -24px 24px -24px"
          }
        }}
        className="ouber-modal white-close-icon"
    >
        <div style={{ marginTop: '24px' }}>
          <Table 
            columns={columns} 
            dataSource={dayTrips} 
            pagination={{ pageSize: 5 }}
            locale={{ emptyText: "No trips scheduled for this date." }}
          />
        </div>
      </Modal>
    );
  };

  const menuItems = useMemo(() => [
    {
      key: "calendar",
      label: "Calendar View",
      icon: <CalendarOutlined />,
      component: (
        <div style={{ background: "#fff", padding: "0", borderRadius: "8px" }}>
          <Space className="ouber-page-header">
            <Typography.Title level={3} className="ouber-page-title">
              Calendar Overview
            </Typography.Title>
            {(isSuperAdmin || userUnits.length > 0) && (
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                className="ouber-btn-forest"
                onClick={() => setIsAddTripModalVisible(true)}
              >
                Create Trip
              </Button>
            )}
          </Space>

          <Alert
            className="ouber-alert"
            message={<span className="ouber-alert-title">Calendar of Scheduled Trips</span>}
            description={
              <span className="ouber-alert-desc">
                View all scheduled trips system-wide on the calendar below. Click on a specific day or a trip card for details.
              </span>
            }
            type="info"
            showIcon
          />

          <div className="ouber-calendar-legend">
            <div className="legend-item"><div className="legend-color legend-available"></div> Available</div>
            <div className="legend-item"><div className="legend-color legend-pending"></div> In Progress</div>
            <div className="legend-item"><div className="legend-color legend-completed"></div> Completed</div>
            <div className="legend-item"><div className="legend-color legend-unavailable"></div> Unavailable/Full Capacity</div>
          </div>

          <Calendar 
            onSelect={(date, { source }) => {
              if (source === 'date') {
                handleDayClick(date);
              }
            }}
            cellRender={dateCellRender} 
            headerRender={calendarHeaderRender} 
          />
        </div>
      ),
    },
    {
      key: "manage-trips",
      label: "Manage Trips",
      icon: <TableOutlined />,
      component: <ManageTripsPage />,
    },
  ], [dateCellRender, calendarHeaderRender, handleDayClick, isSuperAdmin, userUnits]);

  /**
   * Main component render: Handles tab switching between 
   * Calendar View and the administrative Manage Trips table.
   */
  return (
    <div>
      <TabSelection
        menuItems={menuItems}
        currentTabKey="ouberAdminCalTab"
        setTabAction={setOuberAdminTab}
      />
      
      {renderTripModal()}
      {renderDayModal()}

      <AdminAddTripModal 
        open={isAddTripModalVisible}
        onCancel={() => setIsAddTripModalVisible(false)}
        onSave={async (newTrip) => {
          try {
            await ouberService.createTrip(newTrip);
            showSuccessToast("Success", "Trip successfully created!");
            setIsAddTripModalVisible(false);
            loadTrips(unitFilter);
          } catch (e) {
            showErrorToast("Error", "Could not create trip.");
          }
        }}
      />

      <AdminConfirmDeleteTripModal 
        open={isDeleteConfirmVisible}
        onCancel={() => setIsDeleteConfirmVisible(false)}
        tripData={selectedTrip}
        loading={deleteLoading}
        onConfirm={async (trip) => {
          setDeleteLoading(true);
          try {
            await ouberService.deleteTrip(trip.id);
            showSuccessToast("Deleted", "Trip successfully deleted!");
            setIsDeleteConfirmVisible(false);
            loadTrips(unitFilter);
          } catch (e) {
            showErrorToast("Error", "Could not delete trip.");
          } finally {
            setDeleteLoading(false);
          }
        }}
      />

      <AdminEditTripModal 
        open={isEditTripModalVisible}
        onCancel={() => setIsEditTripModalVisible(false)}
        onSave={async (vals) => {
          try {
            await ouberService.updateTrip(selectedTrip.id, vals);
            showSuccessToast("Success", "Trip successfully updated!");
            setIsEditTripModalVisible(false);
            loadTrips(unitFilter);
          } catch (e) {
            showErrorToast("Error", "Could not update trip.");
          }
        }}
        initialValues={selectedTrip}
        onDeleteTrip={(tripId) => {
          setIsEditTripModalVisible(false);
          setIsDeleteConfirmVisible(true);
        }}
        onUpdateSuccess={() => loadTrips(unitFilter)}
      />
    </div>
  );
};

export default AdminCalendarOverviewPage;
